import { load, type CashfreeInstance } from '@cashfreepayments/cashfree-js';

export type GatewayEnv = 'sandbox' | 'production';

/** Fallback only. The server tells us which environment the order lives in. */
const BUILD_MODE: GatewayEnv = import.meta.env.PROD ? 'production' : 'sandbox';

const sdks = new Map<GatewayEnv, Promise<CashfreeInstance | null>>();

/** One SDK instance per environment, loaded on first use. */
function getSdk(mode: GatewayEnv) {
  let sdk = sdks.get(mode);
  if (!sdk) {
    sdk = load({ mode }).catch(() => null);
    sdks.set(mode, sdk);
  }
  return sdk;
}

export type CheckoutOutcome = 'completed' | 'closed' | 'unavailable';

/**
 * Open Cashfree's hosted checkout in a modal over the page.
 *
 * The result here is only a hint about what the shopper did — the booking is
 * confirmed by the server (webhook, or the status poll), never by this value.
 */
export async function openCheckout(
  paymentSessionId: string,
  env: GatewayEnv = BUILD_MODE,
): Promise<CheckoutOutcome> {
  const cashfree = await getSdk(env);
  if (!cashfree) return 'unavailable';
  const result = await cashfree.checkout({
    paymentSessionId,
    redirectTarget: '_modal',
  });
  if (result?.error) return 'closed';
  return 'completed';
}
